import { XBaseContainer } from "./XBaseContainer";
import { IConfigStorage } from "../../rule-engine-core/Common/IConfigStorage";
import { FormControl } from "../Controls/FormControl";
import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import styled from "styled-components";
import { UIControlRegistration } from "../Utilities/RegisterUIControl";

export const OverlapDiv = styled("div")<{ selected?: boolean; border?: string }>`
    background: transparent;
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    z-index: 100;
    filter: alpha(opacity = 0);
    border: ${(props) => (props.selected && props.border ? props.border : "none")};
    border-radius: 2px;
    cursor: initial;
`;

export abstract class XBaseStackContainer extends XBaseContainer implements IConfigStorage {
    innerMargin?: number;
    interControlSpacing?: number;
    protected controls: FormControl[] = [];

    add(control: FormControl, index?: number) {
        // set control order
        if (index === undefined || index === null) {
            this.controls.push(control);
        } else {
            this.controls.splice(index, 0, control);
        }

        if (this.getFormContext()) control.setFormContext(this.getFormContext()!);
    }

    remove(control: FormControl): void {
        const index = this.controls.indexOf(control, 0);
        if (index > -1) {
            this.controls.splice(index, 1);
        }
    }

    setOrder(id: string, order: number) {
        let controlToMove: FormControl | undefined;
        for (let c of this.controls) {
            if (c.id === id) {
                controlToMove = c;
            }
        }

        if (controlToMove) {
            this.remove(controlToMove);
            this.add(controlToMove, order);
        }
    }

    find(id: string): FormControl | null {
        if (Array.isArray(this.controls)) {
            for (let c of this.controls) {
                if (c.id === id) {
                    return c;
                }

                if (c instanceof XBaseContainer) {
                    let found = (c as XBaseContainer).find(id);

                    if (found) return found;
                }
            }
        }
        return null;
    }

    getControls(): FormControl[] {
        return this.controls;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "innerMargin", "interControlSpacing");
        Reflect.set(
            retData,
            "__controls",
            this.controls.map((c) => {
                return c.getStorageData();
            }),
        );
        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "innerMargin", "interControlSpacing");

        let controlArray = Reflect.get(data, "__controls");

        if (controlArray && controlArray instanceof Array && controlArray.length !== 0) {
            for (let idx in controlArray) {
                let controlData = controlArray[idx];
                let typeName = Reflect.get(controlData, "__type");

                if (typeName) {
                    let registeredControl = UIControlRegistration[typeName];
                    registeredControl = UIControlRegistration[typeName];

                    if (!registeredControl) {
                        throw new Error(
                            `${Reflect.get(data, "__type")} > ${Reflect.get(
                                controlData,
                                "label",
                            )}: type [${typeName}] is invalid`,
                        );
                    }

                    let val = new registeredControl.prototype.constructor();
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    let cc = val as FormControl;

                    if (this.getFormContext()) cc.setFormContext(this.getFormContext());

                    cc.setStorageData(controlData);
                    this.controls.push(cc);
                }

                if (!typeName) {
                    throw new Error(
                        `${Reflect.get(data, "__type")} > ${Reflect.get(controlData, "label")}: type is required`,
                    );
                }
            }
        }
    }

    getInnerMargin(): number | undefined {
        return this.innerMargin || this.getFormContext()?.form?.defaultInnerContainerMargin;
    }
}
